// Single place to decide "what name do we show for this user, and how long
// can it be?" so we don't sprinkle (displayName || username) ternaries +
// ad-hoc truncations across every surface that renders a user.

export const NAME_DISPLAY_LIMIT = 30;

const ELLIPSIS = '…';

const clip = (s: string, limit: number): string => {
  if (limit <= 0) return '';
  return s.length > limit ? `${s.slice(0, Math.max(0, limit - 1))}${ELLIPSIS}` : s;
};

interface FormatNameOpts {
  displayName?: string | null;
  username?: string | null;
  limit?: number;
}

/**
 * Pick the best name to show for a user and clip it to a length cap.
 *
 * - displayName wins when present and non-empty (after trim)
 * - falls back to username
 * - returns '' if neither is set
 *
 * Default limit is NAME_DISPLAY_LIMIT (30). Pass a smaller cap on tight
 * surfaces like leaderboard rows or DM previews.
 */
export const formatName = ({
  displayName,
  username,
  limit = NAME_DISPLAY_LIMIT,
}: FormatNameOpts): string => {
  const name = (displayName?.trim() || username?.trim() || '');
  return clip(name, limit);
};

/**
 * Render an "@handle" — always prefixed with @, clipped to the limit.
 * Returns '' if no username is provided.
 */
export const formatHandle = (
  username?: string | null,
  limit: number = NAME_DISPLAY_LIMIT,
): string => {
  const u = username?.trim();
  if (!u) return '';
  return `@${clip(u, limit)}`;
};

/**
 * Clip an already-chosen name string to the limit. Use when the upstream
 * has already coalesced displayName vs username (e.g. a server-side
 * COALESCE in a search query).
 */
export const truncateName = (
  value?: string | null,
  limit: number = NAME_DISPLAY_LIMIT,
): string => {
  if (!value) return '';
  return clip(value.trim(), limit);
};

/**
 * Single character (uppercased) to render in an avatar placeholder.
 * Picks displayName before username; returns '?' when both are empty.
 */
export const nameInitial = (opts: FormatNameOpts): string => {
  const name = (opts.displayName?.trim() || opts.username?.trim() || '');
  return name[0]?.toUpperCase() ?? '?';
};
